<template>
    <form
        ref="form"
        class="mt-4 rounded-2xl border border-foxy-border bg-foxy-surface/60 p-4"
        aria-labelledby="historical-reading-title"
        @submit.prevent="submit"
    >
        <h4 id="historical-reading-title" ref="title" tabindex="-1" class="text-sm font-extrabold outline-none">Додати минуле прочитання</h4>
        <p class="mt-1 text-xs text-foxy-text-secondary">Запис не змінить поточну поличку або актуальне читання.</p>

        <div
            v-if="errors.form || resultError"
            ref="errorSummary"
            tabindex="-1"
            class="mt-3 rounded-xl border border-foxy-error/30 p-3 text-sm text-foxy-error outline-none"
            role="alert"
        >
            {{ resultError || errors.form }}
        </div>

        <fieldset class="mt-4">
            <legend class="text-sm font-bold">Результат</legend>
            <div class="mt-2 flex flex-wrap gap-4">
                <label class="flex min-h-11 items-center gap-2 text-sm"
                    ><input v-model="result" type="radio" value="completed" :disabled="saving" /> Прочитано</label
                >
                <label class="flex min-h-11 items-center gap-2 text-sm"
                    ><input v-model="result" type="radio" value="abandoned" :disabled="saving" /> Не дочитано</label
                >
            </div>
        </fieldset>

        <section class="mt-5 border-t border-foxy-border pt-4">
            <h6 class="text-sm font-extrabold">Оцінка цього прочитання</h6>
            <rating-input
                ref="ratingInput"
                class="mt-3"
                :value="rating"
                label="Оцінка цього прочитання"
                clearable
                :disabled="saving"
                :invalid="Boolean(errors.rating)"
                error-id="historical-reading-rating-error"
                @input="rating = $event"
            />
            <p v-if="errors.rating" id="historical-reading-rating-error" class="mt-2 text-sm text-foxy-error" role="alert">
                {{ errors.rating }}
            </p>
        </section>

        <details ref="details" class="mt-3" @toggle="detailsOpen = $event.target.open">
            <summary class="min-h-11 cursor-pointer py-3 text-sm font-semibold text-foxy-action">Додати деталі (необов’язково)</summary>
            <div class="mt-2">
                <library-reading-language-editor
                    ref="languageSection"
                    reading-id="historical-create"
                    :languages="languages"
                    :primary-language-code="primaryLanguageCode"
                    :canonical-languages="[]"
                    :preferences-state="languagePreferencesState"
                    :full-catalog="languageOptions"
                    allow-catalog-search
                    :saving="saving"
                    :errors="errors.language"
                    @update="updateLanguages"
                    @clear-errors="$emit('clear-errors')"
                    @retry-preferences="$emit('retry-language-preferences')"
                />
                <library-reading-date-editor
                    ref="dateSection"
                    reading-id="historical-create"
                    :state="result || 'completed'"
                    :started="started"
                    :finished="finished"
                    :saving="saving"
                    :errors="dateErrors"
                    @update="updateDate"
                    @clear-errors="$emit('clear-errors')"
                />
                <library-reading-format-editor
                    ref="formatSection"
                    reading-id="historical-create"
                    :rows="formatRows"
                    :saving="saving"
                    :errors="errors.format"
                    @update="formatRows = $event"
                    @clear-errors="$emit('clear-errors')"
                />
            </div>
        </details>

        <div class="mt-4 flex flex-wrap justify-end gap-2 border-t border-foxy-border pt-4">
            <button type="button" class="acc-btn min-h-11" :disabled="saving" @click="$emit('cancel')">Скасувати</button>
            <button ref="saveButton" type="submit" class="acc-btn-primary min-h-11" :disabled="saving">
                {{ saving ? 'Збереження…' : 'Зберегти' }}
            </button>
        </div>
    </form>
</template>

<script lang="ts">
import LibraryReadingDateEditor from './LibraryReadingDateEditor.vue';
import LibraryReadingFormatEditor from './LibraryReadingFormatEditor.vue';
import LibraryReadingLanguageEditor from './LibraryReadingLanguageEditor.vue';
import RatingInput from '../../components/RatingInput.vue';

const emptyPartial = () => ({ precision: 'unknown', year: null, month: null, day: null });

export default {
    name: 'LibraryHistoricalReadingCreateForm',
    components: { LibraryReadingDateEditor, LibraryReadingFormatEditor, LibraryReadingLanguageEditor, RatingInput },
    props: {
        languageOptions: { type: Array, default: () => [] },
        languagePreferencesState: { type: Object, default: () => ({ status: 'idle', data: null, error: '' }) },
        formatOptions: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
        errors: {
            type: Object,
            default: () => ({
                form: '',
                language: { membership: '', primary: '' },
                started: {},
                finished: {},
                dateRange: '',
                format: { group: '', byCode: {} },
                rating: '',
            }),
        },
    },
    data() {
        return {
            result: '',
            resultError: '',
            detailsOpen: false,
            languages: [],
            primaryLanguageCode: null,
            started: emptyPartial(),
            finished: emptyPartial(),
            formatRows: [],
            rating: null,
        };
    },
    computed: {
        dateErrors() {
            return { started: this.errors.started, finished: this.errors.finished, dateRange: this.errors.dateRange };
        },
    },
    created() {
        this.formatRows = this.formatOptions.map((format) => ({
            code: String(format.code || '').toLowerCase(),
            label: format.label || format.code,
            isActiveOption: true,
            wasCanonicalSelection: false,
            selected: false,
            customLabel: '',
        }));
    },
    mounted() {
        this.$nextTick(() => this.$refs.title?.focus());
    },
    methods: {
        updateLanguages(update) {
            this.languages = update.languages || [];
            this.primaryLanguageCode = update.primaryLanguageCode ?? null;
        },
        updateDate(update) {
            if (update?.key === 'started') this.started = update.value;
            if (update?.key === 'finished') this.finished = update.value;
        },
        serializePartial(value) {
            const precision = ['unknown', 'year', 'month', 'day'].includes(value?.precision) ? value.precision : 'unknown';
            return {
                precision,
                year: precision === 'unknown' || value.year === '' ? null : Number(value.year),
                month: ['month', 'day'].includes(precision) && value.month !== '' ? Number(value.month) : null,
                day: precision === 'day' && value.day !== '' ? Number(value.day) : null,
            };
        },
        submit() {
            if (this.saving) return;
            this.resultError = '';
            if (!['completed', 'abandoned'].includes(this.result)) {
                this.resultError = 'Оберіть результат минулого прочитання.';
                this.$nextTick(() => this.$refs.errorSummary?.focus());
                return;
            }
            if (!this.$refs.languageSection?.validate()) return;
            this.$emit('save', {
                result: this.result,
                started: this.serializePartial(this.started),
                finished: this.serializePartial(this.finished),
                languageCodes: this.languages.map((language) => String(language.code || '').toLowerCase()),
                primaryLanguageCode: this.languages.length ? this.primaryLanguageCode : null,
                formats: this.formatRows
                    .filter((format) => format.selected)
                    .map((format) => ({
                        code: String(format.code || '').toLowerCase(),
                        custom_label: String(format.code || '').toLowerCase() === 'other' ? String(format.customLabel || '').trim() || null : null,
                    })),
                rating: this.rating,
            });
        },
        focusError(target = 'form') {
            if (target === 'form') {
                this.$nextTick(() => this.$refs.errorSummary?.focus());
                return;
            }

            this.detailsOpen = true;
            if (this.$refs.details) this.$refs.details.open = true;
            this.$nextTick(() => {
                if (target.startsWith('language.')) {
                    this.$refs.languageSection?.focusValidation(target.split('.')[1]);
                    return;
                }
                if (target.startsWith('started.') || target.startsWith('finished.')) {
                    this.$refs.dateSection?.focusValidation(target);
                    return;
                }
                if (target === 'dateRange') {
                    this.$refs.dateSection?.focusValidation('finished.group');
                    return;
                }
                if (target.startsWith('format.')) {
                    const [, encodedCode = '', field = 'group'] = target.split('.');
                    this.$refs.formatSection?.focusValidation(decodeURIComponent(encodedCode), field);
                    return;
                }
                if (target === 'rating') {
                    this.$refs.ratingInput?.focus();
                    return;
                }
            });
        },
    },
};
</script>
